import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PersonRef } from '@central/contracts';
import { Package, PackagePlus } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatarDataHora } from '@/lib/format';
import { Modal } from '@/features/tickets/Modal';
import { SeletorDePessoa } from '@/features/tickets/pessoas';
import { AvisoDeFalha } from '@/features/workflow/componentes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { deCampoLocal } from '@/features/changes/calendario';
import { chavesDeMudanca, listarJanelas } from '@/features/changes/changes.api';
import { Campo, CLASSE_DE_CAMPO } from '@/features/changes/componentes';
import {
  EtiquetaDeSituacao,
  EtiquetaDeTipoDeRelease,
} from '@/features/releases/componentes';
import { LinkDaEntrega } from '@/features/releases/navegacao';
import { chavesDeRelease, criarRelease, listarReleases } from '@/features/releases/releases.api';
import {
  ROTULO_DE_SITUACAO,
  ROTULO_DE_TIPO_DE_RELEASE,
  SITUACOES_DE_RELEASE,
  TIPOS_DE_RELEASE,
  type SituacaoDeRelease,
  type TipoDeRelease,
} from '@/features/releases/tipos';

/**
 * As versões de entrega.
 *
 * O ITIL separa **mudança** de **release**: a primeira autoriza e controla o
 * risco, a segunda empacota e disponibiliza. Uma versão agrupa N mudanças que
 * sobem juntas — e é isso que permite dizer "a 2026.09 do ERP contém estas sete
 * mudanças", e reverter as sete numa operação só.
 *
 * O filtro por situação é feito no cliente: `GET /releases` não aceita filtro,
 * e o serviço devolve a lista inteira do tenant.
 */
export function ReleasesPage() {
  const [criando, setCriando] = useState(false);
  const [filtro, setFiltro] = useState<SituacaoDeRelease | 'TODAS'>('TODAS');

  const consulta = useQuery({ queryKey: chavesDeRelease.lista, queryFn: listarReleases });

  const visiveis = (consulta.data ?? []).filter(
    (release) => filtro === 'TODAS' || release.status === filtro,
  );

  return (
    <div className="mx-auto max-w-[1400px] space-y-3">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <h1 className="text-base font-semibold">Versões de entrega</h1>
        <Button
          size="sm"
          onClick={() => setCriando(true)}
          icon={<PackagePlus className="h-4 w-4" aria-hidden />}
        >
          Nova versão
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="filtro-situacao" className="text-sm text-muted-foreground">
          Situação
        </label>
        <select
          id="filtro-situacao"
          value={filtro}
          onChange={(evento) => setFiltro(evento.target.value as SituacaoDeRelease | 'TODAS')}
          className={cn(CLASSE_DE_CAMPO, 'w-auto')}
        >
          <option value="TODAS">Todas</option>
          {SITUACOES_DE_RELEASE.map((situacao) => (
            <option key={situacao} value={situacao}>
              {ROTULO_DE_SITUACAO[situacao]}
            </option>
          ))}
        </select>
      </div>

      {consulta.isPending ? (
        <SkeletonRegion label="Carregando versões de entrega" className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </SkeletonRegion>
      ) : null}

      {consulta.isError ? (
        <AvisoDeFalha
          falha={classificarFalha(consulta.error)}
          aoRecarregar={() => void consulta.refetch()}
        />
      ) : null}

      {consulta.data?.length === 0 ? (
        <EmptyState
          icon={<Package className="h-7 w-7" aria-hidden />}
          title="Nenhuma versão de entrega"
          description="Uma versão agrupa mudanças que sobem juntas. Ela não é um chamado: não tem solicitante, não tem SLA e não entra na fila do atendimento."
          action={<Button size="sm" onClick={() => setCriando(true)}>Criar a primeira</Button>}
        />
      ) : null}

      {consulta.data && consulta.data.length > 0 && visiveis.length === 0 ? (
        <EmptyState
          title="Nenhuma versão nesta situação"
          description="O filtro é da tela, não da API: a lista veio inteira e foi recortada aqui."
        />
      ) : null}

      <Card>
        <CardBody className="p-0">
          <ul className="divide-y divide-border">
            {visiveis.map((release) => (
              <li key={release.id} className="px-5 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <LinkDaEntrega
                    releaseId={release.id}
                    className="font-mono text-sm font-semibold text-primary hover:underline"
                  >
                    {release.code}
                  </LinkDaEntrega>
                  <span className="min-w-0 flex-1 truncate font-medium">{release.name}</span>
                  <EtiquetaDeTipoDeRelease tipo={release.type} />
                  <EtiquetaDeSituacao situacao={release.status} />
                </div>
                <p className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                  <span>{release._count.changes} mudança(s)</span>
                  <span>{release._count.cis} item(ns) de configuração</span>
                  {release.plannedDeployAt ? (
                    <span>prevista para {formatarDataHora(release.plannedDeployAt)}</span>
                  ) : (
                    <span>sem data prevista</span>
                  )}
                  {release.rolledBackAt ? (
                    <span className="font-semibold text-destructive">
                      revertida em {formatarDataHora(release.rolledBackAt)}
                    </span>
                  ) : null}
                </p>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>

      {criando ? <DialogoDeRelease aoFechar={() => setCriando(false)} /> : null}
    </div>
  );
}

// ── criação ────────────────────────────────────────────────────────────────

/**
 * Criar a versão.
 *
 * Todos os campos são pedidos aqui porque **não há `PATCH /releases/:id`**: o
 * que não for preenchido agora não tem como ser preenchido depois. A tela diz
 * isso, em vez de deixar a descoberta para o dia em que alguém procurar o botão
 * de editar.
 */
function DialogoDeRelease({ aoFechar }: { aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipo, setTipo] = useState<TipoDeRelease>('MINOR');
  const [responsavel, setResponsavel] = useState<PersonRef | null>(null);
  const [previsao, setPrevisao] = useState('');
  const [janela, setJanela] = useState('');
  const [planoDeImplantacao, setPlanoDeImplantacao] = useState('');
  const [planoDeVerificacao, setPlanoDeVerificacao] = useState('');
  const [planoDeRetorno, setPlanoDeRetorno] = useState('');
  const [falha, setFalha] = useState<Falha | null>(null);

  const janelas = useQuery({
    queryKey: chavesDeMudanca.janelas,
    queryFn: listarJanelas,
    retry: false,
  });

  const criar = useMutation({
    mutationFn: () => {
      if (responsavel === null) throw new Error('Escolha o responsável.');
      const previsaoIso = deCampoLocal(previsao);
      return criarRelease({
        name: nome.trim(),
        ownerId: responsavel.id,
        type: tipo,
        ...(descricao.trim() ? { description: descricao.trim() } : {}),
        ...(previsaoIso ? { plannedDeployAt: previsaoIso } : {}),
        ...(janela ? { changeWindowId: janela } : {}),
        ...(planoDeImplantacao.trim() ? { deploymentPlan: planoDeImplantacao.trim() } : {}),
        ...(planoDeVerificacao.trim() ? { verificationPlan: planoDeVerificacao.trim() } : {}),
        ...(planoDeRetorno.trim() ? { rollbackPlan: planoDeRetorno.trim() } : {}),
      });
    },
    onMutate: () => setFalha(null),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeRelease.raiz });
      aoFechar();
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const incompleto = nome.trim().length < 3 || responsavel === null;

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo="Nova versão de entrega"
      descricao="O código sai do esquema de identificadores do tenant (prefixo REL), com alocação atômica — não se escolhe aqui."
    >
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (!incompleto) criar.mutate();
        }}
      >
        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Não há rota de alteração para versão de entrega: o que ficar em branco agora não terá
          como ser corrigido depois pela interface. Vale preencher os planos aqui.
        </p>

        <Campo rotulo="Nome" htmlFor="release-nome">
          <input
            id="release-nome"
            value={nome}
            onChange={(evento) => setNome(evento.target.value)}
            className={CLASSE_DE_CAMPO}
            placeholder="ERP 2026.09"
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="Tipo" htmlFor="release-tipo">
            <select
              id="release-tipo"
              value={tipo}
              onChange={(evento) => setTipo(evento.target.value as TipoDeRelease)}
              className={CLASSE_DE_CAMPO}
            >
              {TIPOS_DE_RELEASE.map((valor) => (
                <option key={valor} value={valor}>
                  {ROTULO_DE_TIPO_DE_RELEASE[valor]}
                </option>
              ))}
            </select>
          </Campo>

          <Campo rotulo="Implantação prevista" htmlFor="release-previsao">
            <input
              id="release-previsao"
              type="datetime-local"
              value={previsao}
              onChange={(evento) => setPrevisao(evento.target.value)}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>
        </div>

        <div className="space-y-1.5">
          <SeletorDePessoa
            rotulo="Responsável"
            escolhida={responsavel}
            aoEscolher={setResponsavel}
          />
          {responsavel ? (
            <p className="text-xs text-muted-foreground">
              Escolhido: <span className="font-medium text-foreground">{responsavel.name}</span>
            </p>
          ) : null}
        </div>

        <Campo
          rotulo="Janela de mudança"
          htmlFor="release-janela"
          dica="Opcional. A versão pode subir na mesma janela das mudanças que ela agrupa."
        >
          <select
            id="release-janela"
            value={janela}
            onChange={(evento) => setJanela(evento.target.value)}
            className={CLASSE_DE_CAMPO}
          >
            <option value="">Sem janela</option>
            {(janelas.data ?? []).map((opcao) => (
              <option key={opcao.id} value={opcao.id}>
                {opcao.name} · {formatarDataHora(opcao.startsAt)}
              </option>
            ))}
          </select>
        </Campo>

        <Campo rotulo="Descrição" htmlFor="release-descricao">
          <textarea
            id="release-descricao"
            rows={2}
            value={descricao}
            onChange={(evento) => setDescricao(evento.target.value)}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo rotulo="Plano de implantação" htmlFor="release-implantacao">
          <textarea
            id="release-implantacao"
            rows={3}
            value={planoDeImplantacao}
            onChange={(evento) => setPlanoDeImplantacao(evento.target.value)}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo rotulo="Plano de verificação" htmlFor="release-verificacao">
          <textarea
            id="release-verificacao"
            rows={3}
            value={planoDeVerificacao}
            onChange={(evento) => setPlanoDeVerificacao(evento.target.value)}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo rotulo="Plano de retorno" htmlFor="release-retorno">
          <textarea
            id="release-retorno"
            rows={3}
            value={planoDeRetorno}
            onChange={(evento) => setPlanoDeRetorno(evento.target.value)}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        {falha ? <AvisoDeFalha falha={falha} /> : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar} disabled={criar.isPending}>
            Cancelar
          </Button>
          <Button type="submit" loading={criar.isPending} disabled={incompleto}>
            Criar versão
          </Button>
        </div>
      </form>
    </Modal>
  );
}
